import { useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useHomeAgentId } from '../stores/agentsStore';

const AGENT_PREFIX = /^\/agents\/([^/]+)/;

/** A conversation's address says `chat`; inside the app it is still a
 *  `conversation` item, so the two are translated at the edge. */
const toAddress = (rest: string) => rest.replace(/^\/conversation(?=\/)/, '/chat');
const fromAddress = (rest: string) => rest.replace(/^\/chat(?=\/)/, '/conversation');

/** The agent a pathname is under, or null on a route that names none (the
 *  older unprefixed addresses, and the project's own pages). Pure, so the parts
 *  above the routes — the tab strip, the conversation stream — can read it from the
 *  address on the same render, with no effect to wait for. */
export function agentIdFromPath(pathname: string): string | null {
  return AGENT_PREFIX.exec(pathname)?.[1] ?? null;
}

/**
 * Which agent's page this is, and how to link inside it.
 *
 * An agent's screens live under `/agents/:agentId`. The older addresses
 * (`/agent/…`, and the unprefixed `/conversation/…`, `/file/…`, `/home`)
 * redirect there — to the agent the project opens on (`homeAgentId`) — so an
 * old link still lands. Every link the app builds goes through `path`, so it
 * carries the agent it was made in.
 */
export function useAgentScope() {
  const { agentId: routeAgentId } = useParams<{ agentId?: string }>();
  const { activeProject } = useProject();
  const location = useLocation();

  const homeAgentId = useHomeAgentId(activeProject?.id);
  const agentId = routeAgentId ?? homeAgentId;
  const base = agentId ? `/agents/${agentId}` : '';

  /** A path inside this agent's page: `path('/file/123')`,
   *  `path('/conversation/123')` → `/agents/:id/chat/123`. */
  const path = useCallback((rest = '') => `${base}${toAddress(rest)}`, [base]);

  return {
    agentId,
    path,
    /** The route within the agent page, prefix stripped — `/conversation/new`, `/`. */
    subpath: fromAddress(location.pathname.replace(AGENT_PREFIX, '')) || '/',
  };
}
